import './EmptyState.scss'
import type { ComponentProps, ReactNode } from 'react'
import { MdOutlineInbox } from 'react-icons/md'
import { clx } from '../clx'

export interface EmptyStateProps extends Omit<ComponentProps<'div'>, 'title'> {
  title: ReactNode
  description?: ReactNode
  /** Decorative icon (aria-hidden). Defaults to an inbox; pass `null` for none. */
  icon?: ReactNode
  /** Primary action, e.g. a "Create" Button. */
  action?: ReactNode
  /** Smaller, for inside a table or a card. */
  isCompact?: boolean
  /** Element for the title. A full-page empty state may want a heading. Default `p`. */
  titleAs?: 'p' | 'h2' | 'h3' | 'h4'
}

/** "Nothing here yet": an icon, a title, an explanation and the action that fixes it. */
export function EmptyState({
  title,
  description,
  icon = <MdOutlineInbox />,
  action,
  isCompact = false,
  titleAs: Title = 'p',
  className,
  ...rest
}: EmptyStateProps) {
  return (
    <div
      {...rest}
      className={clx('empty-state', isCompact && 'empty-state--is-compact', className)}
    >
      {icon && (
        <span className="empty-state__icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <Title className="empty-state__title">{title}</Title>
      {description && <p className="empty-state__description">{description}</p>}
      {action && <div className="empty-state__action">{action}</div>}
    </div>
  )
}
